import { FC, useCallback, useEffect } from 'react';
import { Form } from 'app/hooks/useGlobalForm';
import CheckboxWithInfoAndValue from 'app/components/CheckboxWithInfoAndValue';
import {
  AccessoriesAvailabilityInterface,
  AccessoriesProductsDTO,
} from 'ks-common';
import sortAccessories from 'utils/sortAccessoriesByPrice';
import {
  isSnackTrayAccessory,
  isSnackTrayPromoDouble,
} from 'app/constants/snackTrayPromo';

interface Props {
  product: Partial<Form>;
  accessoriesAvailability: AccessoriesAvailabilityInterface[];
  error: unknown;
  form: Partial<Form>;
  updateForm: (value: Partial<Form>) => void;
}

const Accessories: FC<Props> = ({
  product,
  accessoriesAvailability,
  error,
  form,
  updateForm,
}) => {
  const options: AccessoriesProductsDTO[] =
    product?.product?.accessoriesProducts || [];

  const selected = form?.accessories?.accessories || [];

  const updateSelectedAccessories = (updated: number[]) => {
    updateForm({
      accessories: {
        accessories: updated,
      },
    });
  };

  useEffect(() => {
    if (accessoriesAvailability && selected.length) {
      let newSelected = [...selected];
      accessoriesAvailability?.forEach(
        (accssr: AccessoriesAvailabilityInterface) => {
          if (!accssr.Available && selected.includes(accssr.AccessoriesID)) {
            newSelected = newSelected.filter(
              (v: number) => v !== accssr.AccessoriesID,
            );
          }
        },
      );
      updateSelectedAccessories(newSelected);
    }
  }, [accessoriesAvailability]);

  const selectValue = (value: number) => {
    const updated = [...selected, value];
    updateSelectedAccessories(updated);
  };

  const deselectValue = (value: number) => {
    const updated = selected.filter((v: number) => v !== value);
    updateSelectedAccessories(updated);
  };

  const checkboxOnChange =
    (option: AccessoriesProductsDTO) =>
    ({ target: { checked } }: React.ChangeEvent<HTMLInputElement>) => {
      if (checked) {
        selectValue(option.AccessoriesID);
      } else {
        deselectValue(option.AccessoriesID);
      }
    };

  const accessoryIsAvailable = useCallback(
    (id: number) =>
      accessoriesAvailability?.find?.(
        (accssr: AccessoriesAvailabilityInterface) =>
          id === accssr?.AccessoriesID,
      )?.Available,
    [accessoriesAvailability],
  );

  const productIsSnackTrayPromoDouble = isSnackTrayPromoDouble(
    product?.product?.ProductID,
  );

  return (
    <>
      {options?.sort(sortAccessories).map((option: AccessoriesProductsDTO) => {
        const checked = selected.indexOf(option.AccessoriesID) !== -1;

        const checkboxInfo = {
          image: option.accessories?.ImagePath,
          price: option.accessories?.Price,
          disabled: false,
        };

        const itemDisabled =
          !!error || !accessoryIsAvailable(option.AccessoriesID);

        const showSnackTrayPromo =
          productIsSnackTrayPromoDouble &&
          isSnackTrayAccessory(option.accessories);

        return (
          <CheckboxWithInfoAndValue
            key={option?.AccessoriesID}
            checked={checked}
            disabled={itemDisabled}
            onChange={checkboxOnChange(option)}
            label={option.accessories?.DisplayName}
            info={checkboxInfo}
            isNew={showSnackTrayPromo}
          />
        );
      })}
    </>
  );
};

export default Accessories;
